<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, provide, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import useVuelidate from '@vuelidate/core'
import { z } from 'zod'
import {
  ApiRequestError,
  getIntakeAddress,
  getIntakeState,
  intakeDocumentFileUrl,
  removeIntakeDocument,
  saveIntakeStep,
  submitIntake,
  suggestIntakeAddresses,
  uploadIntakeDocument,
} from '@/api/client'
import type {
  IntakeDocument,
  IntakeDocumentSlot,
  IntakeProgress,
  IntakeServiceGroup,
} from '@/api/schemas'
import { tidy, type TidyKind } from '@/lib/inputFormat'
import { useToasts } from '@/lib/toasts'
import IntakeStepRenderer from './IntakeStepRenderer.vue'
import { addressLookupKey, type AddressLookup } from './addressLookup'
import { documentHrefKey } from './documentLinks'
import { uploadStatusKey, type UploadStatus } from './uploadStatus'
import UploadList from './UploadList.vue'
import NextSteps from './NextSteps.vue'
import {
  BurnsDefaultsSchema,
  incompleteSteps,
  intakeDefinition,
  missingRecommendedSlots,
  missingRequiredSlots,
  otherFiles,
  slotFiles,
  stepErrors,
  type BurnsDefaults,
  type FieldValidation,
  type IntakeFormData,
  type IntakeFormDefinition,
} from './formDefinitions'

// Route params can be a repeated segment (string[]) or absent; the token is
// otherwise trusted only as an opaque path segment, never parsed further.
const TokenParamSchema = z.string().min(1)

const route = useRoute()
const router = useRouter()
const token = computed(() => {
  const raw = route.params.token
  const parsed = TokenParamSchema.safeParse(Array.isArray(raw) ? raw[0] : raw)
  return parsed.success ? parsed.data : ''
})
const step = ref(0)
// The URL carries the visible step, 1-based like "Step 3 of 6". Every step
// change is a history entry, so a refresh stays put and the browser's back and
// forward buttons walk the steps.
function stepFromUrl(): number | undefined {
  const raw = route.query.step
  const parsed = Number(Array.isArray(raw) ? raw[0] : raw)
  return Number.isInteger(parsed) && parsed >= 1 ? parsed - 1 : undefined
}
function stepLocation(index: number) {
  return { query: { ...route.query, step: String(index + 1) } }
}
// A step from the URL is honoured when it was reachable without skipping
// ahead: no further than the first unsaved or incomplete step, or one already
// saved. Anything else lands on that first unfinished step.
function reachableStep(requested: number | undefined): number {
  if (!definition.value || !form.value) return 0
  const last = definition.value.steps.length - 1
  const firstIncomplete = incompleteSteps(definition.value, form.value)[0] ?? last
  // Autosave marks a step saved as soon as one field changes, so resume at the
  // first step that is unsaved or still incomplete, not just the first unsaved one.
  const resume = Math.min(progress.value.next_step, firstIncomplete, last)
  return requested !== undefined &&
    requested <= last &&
    (requested <= resume || saved.value.has(requested))
    ? requested
    : resume
}
function goToStep(index: number) {
  step.value = index
  void router.push(stepLocation(index))
}
// The on-page Back pops history when the entry before this one is the previous
// step, so it and the browser's back button leave the same trail.
function goBack() {
  const previous = router.resolve(stepLocation(step.value - 1)).fullPath
  const state: unknown = window.history.state
  if (typeof state === 'object' && state !== null && 'back' in state && state.back === previous)
    router.back()
  else goToStep(step.value - 1)
}
// Browser back/forward, or an edited URL. The step being left is autosaved,
// since no field blur may have fired on the way out.
watch(
  () => route.query.step,
  () => {
    // goToStep has already moved to the step it pushed.
    if (!definition.value || loading.value || stepFromUrl() === step.value) return
    const target = reachableStep(stepFromUrl())
    if (target !== step.value) {
      const left = step.value
      step.value = target
      void autosave(left)
    }
    if (stepFromUrl() !== target) void router.replace(stepLocation(target))
  },
)
const loading = ref(true)
const saving = ref(false)
const submitting = ref(false)
const error = ref('')
const toasts = useToasts()
const clientName = ref('')
const progress = ref<IntakeProgress>({
  total_steps: 0,
  saved_steps: [],
  saved_count: 0,
  next_step: 0,
})
const documents = ref<IntakeDocument[]>([])
const documentSlots = ref<IntakeDocumentSlot[]>([])
const submitted = ref(false)
const serverWarnings = ref<string[]>([])
const definition = ref<IntakeFormDefinition>()
const defaults = ref<BurnsDefaults>({})
const services = ref<IntakeServiceGroup[]>([])
const form = ref<IntakeFormData>()
const stillNeededLabels = computed(() =>
  missingRequiredSlots(documentSlots.value, documents.value).map((slot) => slot.label),
)
const worthAddingLabels = computed(() =>
  missingRecommendedSlots(documentSlots.value, documents.value).map((slot) => slot.label),
)
const v$ = useVuelidate(
  computed(() =>
    definition.value && form.value ? definition.value.rules(step.value, form.value) : {},
  ),
  computed(() => form.value ?? {}),
)
// Vuelidate types a field lookup by name as `any`; this is the part the intake reads.
function fieldValidation(key: string) {
  return v$.value[key] as FieldValidation | undefined
}
const saved = computed(() => new Set(progress.value.saved_steps))
const currentStep = computed(() => definition.value?.steps[step.value])
const isReview = computed(
  () => definition.value !== undefined && step.value === definition.value.steps.length - 1,
)
const title = computed(() => currentStep.value?.title ?? 'Intake')

// Autosave: leaving a field saves its step whenever the payload differs from
// what the server last stored, so unchanged steps never gain a saved row or a
// "saved" event. Every write goes through one queue so an older save can never
// land after a newer one.
const lastSaved = new Map<number, string>()
const autosaveStatus = ref<'idle' | 'saving' | 'saved' | 'failed'>('idle')
let saveQueue: Promise<unknown> = Promise.resolve()
let retry: ReturnType<typeof setTimeout> | undefined
let pendingChange: ReturnType<typeof setTimeout> | undefined

function payloadFor(index: number) {
  if (!definition.value || !form.value) return '{}'
  return JSON.stringify(definition.value.serialize(index, form.value))
}
function isUnsaved(index: number) {
  return payloadFor(index) !== lastSaved.get(index)
}
function anyUnsaved() {
  return definition.value?.steps.some((_, index) => isUnsaved(index)) ?? false
}
function persist(index: number, force: boolean) {
  const run = async () => {
    if (!definition.value || (!force && !isUnsaved(index))) return
    const payload = payloadFor(index)
    const result = await saveIntakeStep(
      token.value,
      index,
      z.record(z.string(), z.unknown()).parse(JSON.parse(payload)),
    )
    lastSaved.set(index, payload)
    progress.value = result.progress
    if (autosaveStatus.value === 'failed' && !anyUnsaved()) autosaveStatus.value = 'saved'
  }
  const next = saveQueue.then(run, run)
  saveQueue = next.catch(() => undefined)
  return next
}
async function autosave(index = step.value) {
  clearTimeout(pendingChange)
  if (!definition.value || !isUnsaved(index)) return
  clearTimeout(retry)
  autosaveStatus.value = 'saving'
  try {
    await persist(index, false)
    autosaveStatus.value = 'saved'
  } catch {
    autosaveStatus.value = 'failed'
    retry = setTimeout(() => void autosave(index), 5000)
  }
}
// A burst of checkbox or select changes becomes one save. Mobile Safari never
// focuses a tapped checkbox, so a change cannot wait for the field to be left.
function autosaveSoon() {
  const index = step.value
  clearTimeout(pendingChange)
  pendingChange = setTimeout(() => void autosave(index), 1000)
}
// A submitted link renders no step form and never autosaves, so it can never have unsaved changes.
function warnIfUnsaved(event: BeforeUnloadEvent) {
  if (!submitted.value && (autosaveStatus.value === 'saving' || anyUnsaved()))
    event.preventDefault()
}
function message(cause: unknown, fallback: string) {
  return cause instanceof ApiRequestError ? cause.message : fallback
}

// Delegated touch/recheck: every validated control (or the group wrapping it)
// carries id="field-<key>" (atomic controls) and/or data-field="<key>" (groups).
// Listening once on the form catches every control without wiring each one.
function fieldKey(target: EventTarget | null): string | undefined {
  const el = (target as HTMLElement | null)?.closest<HTMLElement>('[id^="field-"], [data-field]')
  if (!el) return undefined
  return el.dataset.field ?? el.id.slice('field-'.length)
}
function fieldState(target: EventTarget | null) {
  const key = fieldKey(target)
  return key === undefined ? undefined : fieldValidation(key)
}
// A field left valid is reset, so later typing can't raise its error before the next blur.
function touch(field: FieldValidation | undefined) {
  field?.$touch()
  if (field && !field.$invalid) field.$reset()
}
function settleValidFields() {
  if (!definition.value || !form.value) return
  for (const key of Object.keys(definition.value.rules(step.value, form.value))) {
    const field = fieldValidation(key)
    if (field?.$dirty === true && !field.$invalid) field.$reset()
  }
}
function recheckField(event: Event) {
  const target = event.target as HTMLElement
  if (isTidyable(target)) editedSinceFocus.add(target)
  const field = fieldState(event.target)
  if (field?.$dirty === true && !field.$invalid) field.$reset()
}

// Tidying on leave: text-like fields are trimmed/normalized once a field is
// left, but only when the visitor actually edited them this focus, so a
// restored legacy value that's just clicked through is never rewritten (that
// would otherwise mark the step dirty and trigger an autosave on its own).
const editedSinceFocus = new WeakSet<Element>()
function isTidyable(target: EventTarget | null): target is HTMLInputElement | HTMLTextAreaElement {
  if (target instanceof HTMLTextAreaElement) return true
  return (
    target instanceof HTMLInputElement &&
    ['', 'text', 'email', 'url', 'tel', 'search'].includes(target.type)
  )
}
function tidyField(target: EventTarget | null) {
  if (!isTidyable(target) || !editedSinceFocus.has(target) || target.dataset.tidy === 'none') return
  const kind =
    (target.dataset.tidy as TidyKind | undefined) ??
    (target instanceof HTMLInputElement && target.type === 'email' ? 'email' : 'text')
  const next = tidy(kind, target.value)
  if (next !== target.value) {
    target.value = String(next)
    target.dispatchEvent(new Event('input', { bubbles: true }))
  }
  editedSinceFocus.delete(target)
}
// Leaving a field can reveal its error or the save status, shifting everything
// below. When focus left because of a press on another control, that shift
// would land between pointer down and up and swallow the click, so it waits
// until the press (and its click) is over.
let pressing = false
const afterPress: (() => void)[] = []
function startPress() {
  pressing = true
}
function endPress() {
  pressing = false
  setTimeout(() => {
    for (const run of afterPress.splice(0)) run()
  })
}
function markTouched(event: FocusEvent) {
  tidyField(event.target)
  // Moving between controls inside one group (a checklist, an hours table) is not leaving it.
  const group = (event.target as HTMLElement | null)?.closest('[data-field]')
  if (group && event.relatedTarget instanceof Node && group.contains(event.relatedTarget)) return
  const field = fieldState(event.target)
  const index = step.value
  const leave = () => {
    touch(field)
    void autosave(index)
  }
  if (pressing) afterPress.push(leave)
  else leave()
}
function handleFieldChange(event: Event) {
  const target = event.target as HTMLElement
  // Selects and checkboxes are left once changed; an hours table waits until focus leaves it.
  if (target.matches('select, input[type="checkbox"]') && !target.closest('[data-field$="hours"]'))
    touch(fieldState(target))
  else recheckField(event)
  autosaveSoon()
}

async function focusField(key: string | undefined) {
  if (key === undefined) return
  await nextTick()
  const el = document.getElementById('field-' + key)
  el?.scrollIntoView({ block: 'center' })
  el?.focus({ preventScroll: true })
}

// Address suggestions go through the token-gated API, which holds the Google key.
// Provided only once the server says a lookup is configured.
const addressLookup = ref<AddressLookup>()
provide(addressLookupKey, addressLookup)
provide(documentHrefKey, (id) => intakeDocumentFileUrl(token.value, id))

async function load() {
  loading.value = true
  error.value = ''
  try {
    const state = await getIntakeState(token.value)
    const selected = intakeDefinition(state.definition.id)
    if (!selected) throw new Error('This intake link has an unsupported form definition.')
    definition.value = selected
    const parsedDefaults = BurnsDefaultsSchema.safeParse(state.definition.defaults)
    defaults.value = parsedDefaults.success ? parsedDefaults.data : {}
    services.value = state.definition.services ?? []
    const next = selected.initial(defaults.value)
    for (const response of state.responses) selected.restore(response.step, next, response.data)
    form.value = next
    clientName.value = state.client_name
    addressLookup.value =
      state.address_suggestions === true
        ? {
            suggest: (query, session, signal) =>
              suggestIntakeAddresses(token.value, query, session, signal),
            resolve: (placeId, session) => getIntakeAddress(token.value, placeId, session),
          }
        : undefined
    progress.value = state.progress
    documents.value = state.documents
    documentSlots.value = state.definition.documents
    submitted.value = state.submitted
    serverWarnings.value = state.warnings
    lastSaved.clear()
    selected.steps.forEach((_, index) => lastSaved.set(index, payloadFor(index)))
    step.value = reachableStep(stepFromUrl())
    if (stepFromUrl() !== step.value) void router.replace(stepLocation(step.value))
  } catch (cause) {
    error.value =
      cause instanceof Error && cause.message.includes('unsupported form')
        ? cause.message
        : message(cause, 'This intake could not be loaded.')
  } finally {
    loading.value = false
  }
}

async function save(advance: boolean) {
  if (!definition.value || !currentStep.value || !form.value) return
  const valid = advance ? await v$.value.$validate() : true
  if (advance) settleValidFields()
  saving.value = true
  error.value = ''
  try {
    // Force a write only for a step with no saved row yet; otherwise an unchanged payload was already stored.
    await persist(step.value, !saved.value.has(step.value))
    // The autosave line is the only save confirmation; no toast.
    autosaveStatus.value = 'saved'
    if (advance) {
      if (!valid) {
        const invalid = Object.keys(stepErrors(definition.value, step.value, form.value))
        error.value =
          invalid.length === 1
            ? '1 field needs attention before you can continue.'
            : `${invalid.length} fields need attention before you can continue.`
        await focusField(invalid[0])
      } else if (definition.value.blocksFirstAdvance?.(form.value) === true) {
        error.value =
          'A public, verified Google Business Profile at a qualifying physical address is needed before continuing.'
      } else if (!isReview.value) goToStep(step.value + 1)
    }
  } catch (cause) {
    error.value = message(cause, 'This step was not saved. Try again.')
  } finally {
    saving.value = false
  }
}

// An upload or removal confirms twice: a toast at the top of the screen, seen
// wherever the page is scrolled, and the slot's own state at the control that
// was used. A failure shows only at that slot, next to the control to retry.
const uploadStatus = reactive<UploadStatus>({
  uploading: new Map(),
  removing: new Set(),
  justAdded: undefined,
  failure: undefined,
})
provide(uploadStatusKey, uploadStatus)
let justAddedTimer: ReturnType<typeof setTimeout> | undefined

function countUpload(kind: string, change: 1 | -1) {
  const count = (uploadStatus.uploading.get(kind) ?? 0) + change
  if (count > 0) uploadStatus.uploading.set(kind, count)
  else uploadStatus.uploading.delete(kind)
}

async function upload(kind: string, event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0]
  if (!file || !form.value) return
  uploadStatus.failure = undefined
  countUpload(kind, 1)
  try {
    const uploaded = await uploadIntakeDocument(token.value, kind, file)
    documents.value.push({
      id: uploaded.document_id,
      kind,
      filename: uploaded.filename,
      content_type: uploaded.content_type,
      size: uploaded.size,
      uploaded_at: new Date().toISOString(),
    })
    if (!form.value.available_documents.includes(kind)) form.value.available_documents.push(kind)
    toasts.success(`${uploaded.filename} uploaded.`)
    uploadStatus.justAdded = uploaded.document_id
    clearTimeout(justAddedTimer)
    justAddedTimer = setTimeout(() => {
      uploadStatus.justAdded = undefined
    }, 2500)
    if (!submitted.value) void autosave()
  } catch (cause) {
    uploadStatus.failure = {
      kind,
      message: message(cause, 'The document was not uploaded. Try again.'),
    }
  } finally {
    countUpload(kind, -1)
    ;(event.target as HTMLInputElement).value = ''
  }
}

// Drops a kind from available_documents once nothing active still carries it or one of its aliases.
function pruneAvailableDocuments() {
  if (!form.value) return
  form.value.available_documents = form.value.available_documents.filter((kind) => {
    const slot = documentSlots.value.find(
      (candidate) => candidate.kind === kind || candidate.aliases.includes(kind),
    )
    return slot
      ? slotFiles(slot, documents.value).length > 0
      : documents.value.some((document) => document.kind === kind)
  })
}

async function removeDocument(id: number) {
  const doc = documents.value.find((document) => document.id === id)
  if (!doc || uploadStatus.removing.has(id)) return
  uploadStatus.failure = undefined
  uploadStatus.removing.add(id)
  try {
    await removeIntakeDocument(token.value, id)
    documents.value = documents.value.filter((document) => document.id !== id)
    pruneAvailableDocuments()
    toasts.success(`${doc.filename} removed.`)
    if (!submitted.value) void autosave()
  } catch (cause) {
    uploadStatus.failure = {
      documentId: id,
      message: message(cause, 'The document was not removed. Try again.'),
    }
  } finally {
    uploadStatus.removing.delete(id)
  }
}

async function submit() {
  if (!definition.value || !form.value) return
  const reviewIndex = definition.value.steps.length - 1
  const incomplete = incompleteSteps(definition.value, form.value)
  if (incomplete.some((index) => index !== reviewIndex)) {
    error.value = 'Some steps still need attention. Use the links above to finish them.'
    await nextTick()
    document.querySelector<HTMLButtonElement>('.complete-step')?.focus()
    return
  }
  if (incomplete.includes(reviewIndex)) {
    await save(true)
    return
  }
  await save(false)
  if (error.value) return
  submitting.value = true
  error.value = ''
  // The submitted view carries its own confirmation, so no toast is shown here.
  try {
    await submitIntake(token.value)
    submitted.value = true
    // The route doesn't change, so the router never scrolls; without this the
    // shorter submitted view opens at the Submit button's depth, among the uploads.
    await nextTick()
    window.scrollTo(0, 0)
    document.querySelector<HTMLHeadingElement>('.submitted-view h1')?.focus()
  } catch (cause) {
    error.value = message(cause, 'Your intake was saved, but it could not be submitted. Try again.')
  } finally {
    submitting.value = false
  }
}
onMounted(() => {
  window.addEventListener('beforeunload', warnIfUnsaved)
  window.addEventListener('pointerdown', startPress, true)
  window.addEventListener('pointerup', endPress, true)
  window.addEventListener('pointercancel', endPress, true)
  void load()
})
onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', warnIfUnsaved)
  window.removeEventListener('pointerdown', startPress, true)
  window.removeEventListener('pointerup', endPress, true)
  window.removeEventListener('pointercancel', endPress, true)
  clearTimeout(retry)
  clearTimeout(pendingChange)
  clearTimeout(justAddedTimer)
})
</script>

<template>
  <main class="intake-shell">
    <header class="intake-header">
      <a class="brand" href="/" aria-label="Baker Web Solutions"
        >BWS <span>Baker Web Solutions</span></a
      >
      <p>
        LSA setup intake <span v-if="clientName">for {{ clientName }}</span>
      </p>
    </header>
    <section v-if="loading" class="status-card" aria-live="polite">
      Loading your saved intake…
    </section>
    <section v-else-if="error && !definition" class="status-card error" role="alert">
      <p>{{ error }}</p>
      <button type="button" @click="load">Try again</button>
    </section>
    <template v-else-if="definition && submitted">
      <section class="intake-card submitted-view">
        <h1 tabindex="-1">Intake submitted</h1>
        <p class="notice" role="status">
          Your intake has been submitted. Baker Web Solutions will follow up if anything is needed.
        </p>
        <NextSteps :review-url="defaults.review_url" />
        <h2 class="documents-title">Your documents</h2>
        <div class="documents-panel">
          <p v-if="stillNeededLabels.length">
            Still needed: {{ stillNeededLabels.join(', ') }}. Add it below when you have it.
          </p>
          <p v-else>All required documents are in. You can still add or remove files below.</p>
          <p v-if="worthAddingLabels.length">
            Worth adding if you have them: {{ worthAddingLabels.join(', ') }}.
          </p>
        </div>
        <UploadList
          :slots="documentSlots"
          :documents="documents"
          :others="otherFiles(documentSlots, documents)"
          @upload="upload"
          @remove="removeDocument"
        />
      </section>
    </template>
    <template v-else-if="definition && currentStep && form">
      <div class="progress" aria-label="Form progress">
        <span>Step {{ step + 1 }} of {{ definition.steps.length }}</span>
        <div>
          <i
            v-for="n in definition.steps.length"
            :key="n"
            :class="{ done: saved.has(n - 1), current: step === n - 1 }"
          />
        </div>
        <span>{{ progress.saved_count }} saved</span>
      </div>
      <div v-for="warning in serverWarnings" :key="warning" class="blocker" role="alert">
        {{ warning }}
      </div>
      <form
        novalidate
        class="intake-card"
        @submit.prevent="isReview ? submit() : save(true)"
        @focusout="markTouched"
        @change="handleFieldChange"
        @input="recheckField"
      >
        <h1>{{ title }}</h1>
        <IntakeStepRenderer
          v-model:form="form"
          :step="currentStep"
          :step-index="step"
          :definition="definition"
          :documents="documents"
          :document-slots="documentSlots"
          :saved-steps="saved"
          :defaults="defaults"
          :services="services"
          :field-validation="fieldValidation"
          @upload="upload"
          @remove="removeDocument"
          @edit="goToStep($event)"
        />
        <p v-if="error" class="form-error" role="alert">{{ error }}</p>
        <p class="autosave" :class="{ failed: autosaveStatus === 'failed' }" aria-live="polite">
          {{
            autosaveStatus === 'saving'
              ? 'Saving…'
              : autosaveStatus === 'saved'
                ? 'All changes saved.'
                : autosaveStatus === 'failed'
                  ? 'Your last change was not saved. Retrying…'
                  : ''
          }}
        </p>
        <footer>
          <button
            v-if="step > 0"
            type="button"
            class="secondary arrow back"
            aria-label="Back"
            title="Back"
            :disabled="saving || submitting"
            @click="goBack"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path
                d="M19 12H5M11 6l-6 6 6 6"
                fill="none"
                stroke="currentColor"
                stroke-width="2.25"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg></button
          ><button
            v-if="!isReview"
            type="submit"
            class="arrow"
            aria-label="Save and continue"
            title="Save and continue"
            :aria-busy="saving"
            :disabled="saving"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path
                d="M5 12h14M13 6l6 6-6 6"
                fill="none"
                stroke="currentColor"
                stroke-width="2.25"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg></button
          ><button v-else type="submit" :disabled="saving || submitting">
            {{ submitting ? 'Submitting…' : 'Submit intake' }}
          </button>
        </footer>
      </form>
    </template>
    <p class="contact">
      Questions about this intake? Contact Dallan at Baker Web Solutions through the contact details
      already provided in your invitation.
    </p>
  </main>
</template>

<style scoped>
.intake-shell {
  --ink: #17221d;
  --pine: #285341;
  --lime: #b9c967;
  --paper: #fbfaf4;
  --line: #cad2c4;
  max-width: 820px;
  margin: auto;
  padding: 1.25rem 1rem 3rem;
  color: var(--ink);
  font-family: ui-sans-serif, system-ui, sans-serif;
}
.intake-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  border-bottom: 2px solid var(--pine);
  padding: 0.4rem 0 0.8rem;
}
.brand {
  font-weight: 800;
  color: var(--pine);
  font-size: 1.3rem;
}
.brand span {
  font-weight: 500;
  font-size: 0.82rem;
  margin-left: 0.3rem;
}
.intake-header p,
.contact {
  font-size: 0.85rem;
  color: #526057;
  margin: 0;
}
.progress {
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  margin: 1.5rem 0 0.7rem;
  font-size: 0.82rem;
}
.progress > div {
  display: flex;
  gap: 4px;
  flex: 1;
  max-width: 290px;
}
.progress i {
  height: 7px;
  flex: 1;
  background: #dfe4da;
}
.progress i.done {
  background: var(--pine);
}
.progress i.current {
  background: var(--lime);
}
.blocker {
  background: #f0f3dd;
  border-left: 4px solid #83902e;
  padding: 0.8rem 1rem;
  margin: 0.7rem 0;
}
.intake-card,
.status-card {
  background: var(--paper);
  border: 1px solid var(--line);
  box-shadow: 0 8px 22px #25352a12;
  padding: clamp(1rem, 4vw, 2.2rem);
}
h1 {
  font-size: clamp(1.7rem, 5vw, 2.5rem);
  line-height: 1.1;
  margin: 0 0 0.6rem;
  color: var(--pine);
}
footer {
  display: flex;
  gap: 0.7rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
  border-top: 1px solid var(--line);
  padding-top: 1rem;
}
button {
  background: var(--pine);
  color: white;
  border: 1px solid var(--pine);
  border-radius: 3px;
  padding: 0.65rem 1rem;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}
button.arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 3.25rem;
}
button.back {
  margin-right: auto;
}
button.secondary {
  background: white;
  color: var(--pine);
}
button:focus-visible {
  outline: 3px solid var(--lime);
  outline-offset: 2px;
}
button:disabled {
  opacity: 0.55;
  cursor: wait;
}
.form-error {
  background: #fbe8e5;
  border-left: 4px solid #a53c28;
  padding: 0.7rem;
}
.notice {
  background: #e7f0df;
  border-left: 4px solid var(--pine);
  padding: 0.7rem;
}
.submitted-view {
  display: grid;
  gap: 1rem;
}
.submitted-view > p {
  margin: 0;
  line-height: 1.5;
}
/* Focused on submit only to move screen readers to the confirmation. */
.submitted-view h1:focus {
  outline: none;
}
.documents-title {
  color: var(--pine);
  font-size: 1.05rem;
  margin: 0.5rem 0 -0.5rem;
}
.documents-panel {
  background: #f4f6f0;
  border: 1px solid var(--line);
  padding: 0.8rem 1rem;
  display: grid;
  gap: 0.4rem;
}
.documents-panel p {
  margin: 0;
  font-size: 0.9rem;
  color: #3b4a3d;
}
.contact {
  margin: 1rem 0;
  text-align: center;
}
.autosave {
  min-height: 1.2em;
  margin: 0.6rem 0 0;
  font-size: 0.82rem;
  color: #526057;
}
.autosave.failed {
  color: #a53c28;
  font-weight: 650;
}
@media (max-width: 600px) {
  .intake-header {
    display: block;
  }
  .intake-header p {
    margin-top: 0.25rem;
  }
  .progress {
    gap: 0.5rem;
  }
  .progress span:last-child {
    display: none;
  }
  footer {
    flex-wrap: wrap;
    justify-content: stretch;
  }
  footer button {
    flex: 1;
  }
}
</style>
